import type { User as FirebaseUser } from "@firebase/auth";
import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import PropTypes from "prop-types";
import type { Validator } from "prop-types";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useEffect, useReducer } from "react";
import {
  createUserIfNotExists,
  getUserServerData,
} from "src/api/auth/authhelpers";
import { ensureNightscoutV2Subject } from "src/api/nightscout-subject";
import { firebaseApp } from "src/libs/firebase";
import type { User, UserT1 } from "src/types/user";
import { Issuer } from "src/utils/auth";

export const auth = getAuth(firebaseApp);

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | UserT1 | null;
}

export enum ActionType {
  AUTH_STATE_CHANGED = "AUTH_STATE_CHANGED",
}

export type AuthStateChangedAction = {
  type: ActionType.AUTH_STATE_CHANGED;
  payload: {
    isAuthenticated: boolean;
    user: User | UserT1 | null;
  };
};

type Action = AuthStateChangedAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state: State, action: Action): State => {
  if (action.type === "AUTH_STATE_CHANGED") {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

export interface AuthContextType extends State {
  issuer: Issuer.Firebase;
  createUserWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<any>;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  signInWithFacebook: () => Promise<any>;
  signInWithGithub: () => Promise<any>;
  signOut: () => Promise<void>;
  dispatch: (value: Action) => void;
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  issuer: Issuer.Firebase,
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  signInWithFacebook: () => Promise.resolve(),
  signInWithGithub: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  dispatch: () => undefined,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const getAccessToken = async (user: FirebaseUser) => {
    try {
      const idTokenResult = await user.getIdTokenResult();
      localStorage.setItem("access_token", idTokenResult.token);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAuthStateChanged = useCallback(
    async (user: FirebaseUser | null) => {
      if (user) {
        // Here you should extract the complete user profile to make it available in your entire app.
        // The auth state only provides basic information.
        await getAccessToken(user);
        await createUserIfNotExists(user);
        const userServerData = await getUserServerData(user.uid);
        if (userServerData) {
          await ensureNightscoutV2Subject(userServerData);
        }

        dispatch({
          type: ActionType.AUTH_STATE_CHANGED,
          payload: {
            isAuthenticated: true,
            user: {
              id: user.uid,
              avatar: user.photoURL || undefined,
              email: user.email || "",
              name: user.displayName || "",
              plan: "",

              ...userServerData,
            },
          },
        });
      } else {
        dispatch({
          type: ActionType.AUTH_STATE_CHANGED,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    },
    [dispatch]
  );

  useEffect(() => onAuthStateChanged(auth, handleAuthStateChanged), []);

  const _signInWithEmailAndPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    []
  );

  const signInWithGoogle = useCallback(async (): Promise<void> => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(auth, provider);
  }, []);

  const signInWithFacebook = useCallback(async (): Promise<void> => {
    const provider = new FacebookAuthProvider();
    await signInWithPopup(auth, provider);
  }, []);

  const signInWithGithub = useCallback(async (): Promise<void> => {
    const provider = new GithubAuthProvider();
    await signInWithPopup(auth, provider);
  }, []);

  const _createUserWithEmailAndPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      await createUserWithEmailAndPassword(auth, email, password);
    },
    []
  );

  const _signOut = useCallback(async (): Promise<void> => {
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.Firebase,
        createUserWithEmailAndPassword: _createUserWithEmailAndPassword,
        signInWithEmailAndPassword: _signInWithEmailAndPassword,
        signInWithGoogle,
        signInWithFacebook,
        signInWithGithub,
        signOut: _signOut,
        dispatch: dispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired as Validator<ReactNode>,
};

export const AuthConsumer = AuthContext.Consumer;
